// redux/ugc/momentSlice.ts

import { RootState } from "../../redux/storeTypes";
import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";

import type {
  MomentDTO,
  MomentFeedResponse,
  CreateMomentPayload,
  UpdateMomentPayload,
} from "../../types/ugc/momentTypes";

import { momentApi } from "../../services/ugc/momentApi";

/**
 * NOTE:
 * - Feed is cursor-based.
 * - Explore / Me are page-based.
 * - Moment media now supports:
 *   legacy single image, video, and JSON-backed multi-photo image_items.
 */

// ------------------------------------------------------------
// State Shape
// ------------------------------------------------------------
interface MomentState {
  bySlug: Record<string, MomentDTO>;

  feed: {
    ids: string[];
    next: string | null;
    loading: boolean;
    error?: string;
  };

  explore: {
    ids: string[];
    page: number;
    next: string | null;
    hasMore: boolean;
    loading: boolean;
    error?: string;
  };

  me: {
    ids: string[];
    page: number;
    next: string | null;
    hasMore: boolean;
    loading: boolean;
    error?: string;
  };

  createStatus: "idle" | "loading" | "succeeded" | "failed";
  deleteStatus: "idle" | "loading" | "succeeded" | "failed";
  updateStatus: "idle" | "loading" | "succeeded" | "failed";

  opError?: string;
}

// ------------------------------------------------------------
// Initial State
// ------------------------------------------------------------
const initialState: MomentState = {
  bySlug: {},

  feed: {
    ids: [],
    next: null,
    loading: false,
    error: undefined,
  },

  explore: {
    ids: [],
    page: 1,
    next: null,
    hasMore: true,
    loading: false,
    error: undefined,
  },

  me: {
    ids: [],
    page: 1,
    next: null,
    hasMore: true,
    loading: false,
    error: undefined,
  },

  createStatus: "idle",
  deleteStatus: "idle",
  updateStatus: "idle",
  opError: undefined,
};

// ------------------------------------------------------------
// Thunks
// ------------------------------------------------------------
export const fetchMomentFeed = createAsyncThunk<
  MomentFeedResponse,
  { cursor?: string } | void
>("moments/fetchFeed", async (arg) => {
  return momentApi.getFeed(arg?.cursor);
});

export const fetchMomentExplore = createAsyncThunk<
  MomentFeedResponse,
  { page?: number } | void,
  { state: RootState }
>("moments/fetchExplore", async (arg, { getState }) => {
  const s = getState();
  const pageFromState = s.moments?.explore?.page ?? 1;
  const page = arg?.page ?? pageFromState;
  return momentApi.getExplore(page);
});

export const fetchMyMoments = createAsyncThunk<
  MomentFeedResponse,
  { page?: number } | void,
  { state: RootState }
>("moments/fetchMine", async (arg, { getState }) => {
  const s = getState();
  const pageFromState = s.moments?.me?.page ?? 1;
  const page = arg?.page ?? pageFromState;
  return momentApi.getMyMoments(page);
});

export const fetchMomentBySlug = createAsyncThunk<MomentDTO, string>(
  "moments/fetchBySlug",
  async (slug) => {
    return momentApi.getBySlug(slug);
  }
);

export const createMoment = createAsyncThunk<
  MomentDTO,
  {
    payload: FormData | CreateMomentPayload;
    onUploadProgress?: (pct: number) => void;
    signal?: AbortSignal;
  }
>("moments/create", async ({ payload, onUploadProgress, signal }) => {
  return momentApi.create(payload, { onUploadProgress, signal });
});

export const deleteMomentBySlug = createAsyncThunk<
  string,
  string,
  { state: RootState; rejectValue: { slug: string; snapshot?: MomentDTO } }
>("moments/delete", async (slug, { getState, rejectWithValue }) => {
  try {
    await momentApi.removeBySlug(slug);
    return slug;
  } catch {
    const snapshot = getState().moments.bySlug[slug];
    return rejectWithValue({ slug, snapshot });
  }
});

export const updateMomentBySlug = createAsyncThunk<
  MomentDTO,
  {
    slug: string;
    payload: FormData | UpdateMomentPayload;
    onUploadProgress?: (pct: number) => void;
    signal?: AbortSignal;
  },
  { rejectValue: unknown }
>(
  "moments/update",
  async ({ slug, payload, onUploadProgress, signal }, { rejectWithValue }) => {
    try {
      return await momentApi.updateBySlug(slug, payload, {
        onUploadProgress,
        signal,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// ------------------------------------------------------------
// Helpers
// ------------------------------------------------------------
const addUniqueFront = (arr: string[], id: string) => {
  const i = arr.indexOf(id);
  if (i !== -1) arr.splice(i, 1);
  arr.unshift(id);
};

const addUniqueBack = (arr: string[], id: string) => {
  if (!arr.includes(id)) arr.push(id);
};

const removeFromAllLists = (state: MomentState, slug: string) => {
  state.feed.ids = state.feed.ids.filter((x) => x !== slug);
  state.explore.ids = state.explore.ids.filter((x) => x !== slug);
  state.me.ids = state.me.ids.filter((x) => x !== slug);
};

const upsertMoment = (state: MomentState, moment: MomentDTO) => {
  if (!moment?.slug) return;
  state.bySlug[moment.slug] = moment;
};

const getRequestedPage = (
  metaArg: { page?: number } | void,
  fallback: number
) => {
  if (metaArg && typeof (metaArg as any).page === "number") {
    const p = (metaArg as any).page as number;
    return p > 0 ? p : fallback;
  }
  return fallback;
};

// ------------------------------------------------------------
// Slice
// ------------------------------------------------------------
const momentSlice = createSlice({
  name: "moments",
  initialState,
  reducers: {
    resetMomentState: () => initialState,

    clearFeed(state) {
      state.feed.ids = [];
      state.feed.next = null;
      state.feed.error = undefined;
    },

    resetExplore(state) {
      state.explore.ids = [];
      state.explore.page = 1;
      state.explore.next = null;
      state.explore.hasMore = true;
      state.explore.error = undefined;
    },

    resetMe(state) {
      state.me.ids = [];
      state.me.page = 1;
      state.me.next = null;
      state.me.hasMore = true;
      state.me.error = undefined;
    },

    resetMomentOps(state) {
      state.createStatus = "idle";
      state.deleteStatus = "idle";
      state.updateStatus = "idle";
      state.opError = undefined;
    },

    updateMomentReactions(
      state,
      action: PayloadAction<{
        slug: string;
        reactions_count: number;
        reactions_breakdown: Record<string, number>;
      }>
    ) {
      const moment = state.bySlug[action.payload.slug];
      if (!moment) return;

      moment.reactions_count = action.payload.reactions_count;
      moment.reactions_breakdown = action.payload.reactions_breakdown;
    },
  },

  extraReducers: (builder) => {
    // --------------------------------------------------------
    // Feed
    // --------------------------------------------------------
    builder
      .addCase(fetchMomentFeed.pending, (state) => {
        state.feed.loading = true;
        state.feed.error = undefined;
      })
      .addCase(fetchMomentFeed.fulfilled, (state, action) => {
        const { results, next } = action.payload;

        results.forEach((moment) => {
          upsertMoment(state, moment);
          addUniqueBack(state.feed.ids, moment.slug);
        });

        state.feed.next = next ?? null;
        state.feed.loading = false;
      })
      .addCase(fetchMomentFeed.rejected, (state, action) => {
        state.feed.loading = false;
        state.feed.error = action.error.message || "Failed to load feed.";
      });

    // --------------------------------------------------------
    // Explore
    // --------------------------------------------------------
    builder
      .addCase(fetchMomentExplore.pending, (state) => {
        state.explore.loading = true;
        state.explore.error = undefined;
      })
      .addCase(fetchMomentExplore.fulfilled, (state, action) => {
        const { results, next } = action.payload;
        const requestedPage = getRequestedPage(action.meta.arg, state.explore.page);

        if (requestedPage <= 1) {
          state.explore.ids = [];
        }

        results.forEach((moment) => {
          upsertMoment(state, moment);
          addUniqueBack(state.explore.ids, moment.slug);
        });

        state.explore.next = next ?? null;
        state.explore.hasMore = Boolean(next);

        if (state.explore.hasMore) {
          state.explore.page = requestedPage + 1;
        }

        state.explore.loading = false;
      })
      .addCase(fetchMomentExplore.rejected, (state, action) => {
        state.explore.loading = false;
        state.explore.error = action.error.message || "Failed to load explore.";
      });

    // --------------------------------------------------------
    // My Moments
    // --------------------------------------------------------
    builder
      .addCase(fetchMyMoments.pending, (state) => {
        state.me.loading = true;
        state.me.error = undefined;
      })
      .addCase(fetchMyMoments.fulfilled, (state, action) => {
        const { results, next } = action.payload;
        const requestedPage = getRequestedPage(action.meta.arg, state.me.page);

        if (requestedPage <= 1) {
          state.me.ids = [];
        }

        results.forEach((moment) => {
          upsertMoment(state, moment);
          addUniqueBack(state.me.ids, moment.slug);
        });

        state.me.next = next ?? null;
        state.me.hasMore = Boolean(next);

        if (state.me.hasMore) {
          state.me.page = requestedPage + 1;
        }

        state.me.loading = false;
      })
      .addCase(fetchMyMoments.rejected, (state, action) => {
        state.me.loading = false;
        state.me.error = action.error.message || "Failed to load your moments.";
      });

    // --------------------------------------------------------
    // Retrieve
    // --------------------------------------------------------
    builder.addCase(fetchMomentBySlug.fulfilled, (state, action) => {
      upsertMoment(state, action.payload);
    });

    // --------------------------------------------------------
    // Create
    // --------------------------------------------------------
    builder
      .addCase(createMoment.pending, (state) => {
        state.createStatus = "loading";
        state.opError = undefined;
      })
      .addCase(createMoment.fulfilled, (state, action) => {
        const moment = action.payload;

        upsertMoment(state, moment);

        addUniqueFront(state.feed.ids, moment.slug);
        addUniqueFront(state.me.ids, moment.slug);

        state.createStatus = "succeeded";
      })
      .addCase(createMoment.rejected, (state, action) => {
        state.createStatus = "failed";
        state.opError = action.error.message || "Failed to create moment.";
      });

    // --------------------------------------------------------
    // Delete
    // --------------------------------------------------------
    builder
      .addCase(deleteMomentBySlug.pending, (state, action) => {
        const slug = action.meta.arg;

        state.deleteStatus = "loading";
        state.opError = undefined;

        if (state.bySlug[slug]) {
          delete state.bySlug[slug];
          removeFromAllLists(state, slug);
        }
      })
      .addCase(deleteMomentBySlug.fulfilled, (state) => {
        state.deleteStatus = "succeeded";
      })
      .addCase(deleteMomentBySlug.rejected, (state, action) => {
        state.deleteStatus = "failed";

        const payload = action.payload as
          | { slug: string; snapshot?: MomentDTO }
          | undefined;

        if (payload?.snapshot) {
          const snapshot = payload.snapshot;
          upsertMoment(state, snapshot);
          addUniqueFront(state.feed.ids, snapshot.slug);
          addUniqueFront(state.me.ids, snapshot.slug);
        }

        state.opError = action.error.message || "Failed to delete moment.";
      });

    // --------------------------------------------------------
    // Update
    // --------------------------------------------------------
    builder
      .addCase(updateMomentBySlug.pending, (state) => {
        state.updateStatus = "loading";
        state.opError = undefined;
      })
      .addCase(updateMomentBySlug.fulfilled, (state, action) => {
        upsertMoment(state, action.payload);
        state.updateStatus = "succeeded";
      })
      .addCase(updateMomentBySlug.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.opError = action.error.message || "Failed to update moment.";
      });
  },
});

// ------------------------------------------------------------
// Exports
// ------------------------------------------------------------
export const {
  resetMomentState,
  clearFeed,
  resetExplore,
  resetMe,
  resetMomentOps,
  updateMomentReactions,
} = momentSlice.actions;

export default momentSlice.reducer;